import { z } from "zod";
import { im30SaleResponseSchema } from "./im30";

export const BRIDGE_DEFAULT_PORT = 8787;
export const BRIDGE_DEFAULT_HOST = "127.0.0.1";

// Per-device runtime configuration. On Android this is supplied by the
// native side through AndroidBridge.config(); on desktop dev it comes from
// Vite env vars. It is never baked into a deployed SPA bundle — the kiosk
// SPA is served from a public CloudFront origin, so a token in the bundle
// would be a published credential.
export const kioskRuntimeConfigSchema = z.object({
  backendBaseUrl: z.string().url(),
  terminalToken: z.string().min(1),
});
export type KioskRuntimeConfig = z.infer<typeof kioskRuntimeConfigSchema>;

export const saleRequestSchema = z.object({
  amountCLP: z.number().int().positive(),
  ticketNumber: z.string().length(20),
  sendStatusMessages: z.boolean().optional(),
  requestVoucher: z.boolean().optional(),
});
export type SaleRequest = z.infer<typeof saleRequestSchema>;

export const saleOutcomeSchema = z.discriminatedUnion("outcome", [
  z.object({
    outcome: z.literal("approved"),
    response: im30SaleResponseSchema,
  }),
  z.object({
    outcome: z.literal("declined"),
    response: im30SaleResponseSchema,
  }),
  z.object({
    outcome: z.literal("cancelled"),
    rawResponse: im30SaleResponseSchema.optional(),
  }),
  z.object({
    outcome: z.literal("timeout"),
    recoveredVia: z.enum(["last_sale_match", "last_sale_mismatch", "none"]),
    rawResponse: im30SaleResponseSchema.optional(),
  }),
  z.object({
    outcome: z.literal("link_error"),
    detail: z.string(),
  }),
]);
export type SaleOutcome = z.infer<typeof saleOutcomeSchema>;

export const printJobSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("voucher"),
    text: z.string(),
    cutAtEnd: z.boolean().optional(),
  }),
  z.object({
    kind: z.literal("ticket"),
    museumName: z.string(),
    ticketTypeName: z.string(),
    visitDate: z.string(),
    displayTicketNumber: z.string(),
    uniqueCode: z.string(),
    priceCLP: z.number().int().nonnegative(),
    orderId: z.number().int().positive(),
    last4: z
      .string()
      .regex(/^\d{4}$/)
      .nullable(),
  }),
]);
export type PrintJob = z.infer<typeof printJobSchema>;

export const printResponseSchema = z.object({
  ok: z.boolean(),
  error: z.string().optional(),
});
export type PrintResponse = z.infer<typeof printResponseSchema>;

// Health shape for a print-only service (the standalone staff thermal
// printer agent). Unlike `healthResponseSchema`, it carries no IM30 or
// pending-confirms fields — that service only prints.
export const printHealthResponseSchema = z.object({
  version: z.string(),
  printer: z.object({
    connected: z.boolean(),
    lastPrintAt: z.string().nullable(),
  }),
});
export type PrintHealthResponse = z.infer<typeof printHealthResponseSchema>;

export const lastSaleResponseSchema = z.object({
  available: z.boolean(),
  response: im30SaleResponseSchema.optional(),
});
export type LastSaleResponse = z.infer<typeof lastSaleResponseSchema>;

export const closeResponseSchema = z.object({
  ok: z.boolean(),
  responseCode: z.string().regex(/^\d{2}$/),
  voucher: z.string().nullable(),
});
export type CloseResponse = z.infer<typeof closeResponseSchema>;

export const posSaleRequestSchema = z.object({
  amountCLP: z.number().int().positive().max(999_999_999), // Transbank amount field is 9 digits wide
  ticketNumber: z.string().regex(/^\d{1,6}$/),  // orderId as a ≤6-digit numeric ticket; reused for getLastSale matching
});
export type PosSaleRequest = z.infer<typeof posSaleRequestSchema>;

export const posSaleResponseSchema = z.object({
  functionCode: z.number().int().optional(),
  responseCode: z.number().int(),              // 0 == approved
  successful: z.boolean().optional(),
  responseMessage: z.string().nullable().optional(),
  commerceCode: z.number().int().optional(),
  terminalId: z.string().optional(),
  ticket: z.string().optional(),
  authorizationCode: z.string().nullable().optional(),
  amount: z.number().int().nonnegative().optional(),
  sharesNumber: z.string().optional(),
  sharesAmount: z.string().optional(),
  last4Digits: z.number().int().nullable().optional(),
  operationNumber: z.string().optional(),
  cardType: z.string().optional(),
  accountingDate: z.string().optional(),
  accountNumber: z.string().optional(),
  cardBrand: z.string().optional(),
  realDate: z.string().optional(),
  realTime: z.string().optional(),
  employeeId: z.string().optional(),
  tip: z.number().int().nullable().optional(),
});
export type PosSaleResponse = z.infer<typeof posSaleResponseSchema>;

// Immutable print-service runtime evidence attached to POS sale outcomes so
// staff payment recording can store what the bridge was running when the
// terminal settled (or failed to settle) the charge.
export const posRuntimeSnapshotSchema = z.object({
  printServiceVersion: z.string(),
  port: z.string().nullable(),
  baudRate: z.number().int().positive(),
  autoconnect: z.boolean(),
  saleTimeoutMs: z.number().int().positive(),
});
export type PosRuntimeSnapshot = z.infer<typeof posRuntimeSnapshotSchema>;

export const posSaleOutcomeSchema = z.discriminatedUnion("outcome", [
  z.object({
    outcome: z.literal("approved"),
    response: posSaleResponseSchema,
    runtimeSnapshot: posRuntimeSnapshotSchema.optional(),
  }),
  z.object({
    outcome: z.literal("declined"),
    response: posSaleResponseSchema,
    runtimeSnapshot: posRuntimeSnapshotSchema.optional(),
  }),
  z.object({
    outcome: z.literal("cancelled"),
    response: posSaleResponseSchema.optional(),
    runtimeSnapshot: posRuntimeSnapshotSchema.optional(),
  }),
  z.object({
    outcome: z.literal("timeout"),
    recoveredVia: z.enum(["last_sale_match", "last_sale_mismatch", "none"]),
    response: posSaleResponseSchema.optional(),
    runtimeSnapshot: posRuntimeSnapshotSchema.optional(),
  }),
  z.object({
    outcome: z.literal("link_error"),
    detail: z.string(),
    // "connect": the terminal never answered the (re)connect, so no sale was
    // sent. "sale": the sale command itself failed. Absent from older
    // print-service builds — treat like "sale".
    phase: z.enum(["connect", "sale"]).optional(),
    runtimeSnapshot: posRuntimeSnapshotSchema.optional(),
  }),
]);
export type PosSaleOutcome = z.infer<typeof posSaleOutcomeSchema>;

export const posHealthResponseSchema = z.object({
  version: z.string(),
  pos: z.object({ connected: z.boolean(), port: z.string().nullable(), lastSaleAt: z.string().nullable() }),
});
export type PosHealthResponse = z.infer<typeof posHealthResponseSchema>;

// --- POS Integrado admin commands (homologation menu) -----------------------
// All response fields beyond `ok` are optional so an older print-service that
// returns a bare { ok } still parses in a newer web client (the two deploy
// independently; the wire contract only ever grows additively).

// Anulación: the operation number printed on the original sale voucher
// (SaleResponse.operationNumber), ≤6 digits like the sale ticket.
export const posRefundRequestSchema = z.object({
  operationNumber: z.string().regex(/^\d{1,6}$/),
});
export type PosRefundRequest = z.infer<typeof posRefundRequestSchema>;

// Shared shape for terminal-acknowledged admin commands (loadKeys, close,
// salesDetail): ok mirrors the terminal's successful flag; responseCode /
// responseMessage carry the terminal's verdict (e.g. 3 "Conexion Fallo",
// 11 "no sales") so the UI can show Transbank's recommended glosses.
export const posAdminCommandResponseSchema = z.object({
  ok: z.boolean(),
  responseCode: z.number().int().optional(),
  responseMessage: z.string().nullable().optional(),
});
export type PosAdminCommandResponse = z.infer<
  typeof posAdminCommandResponseSchema
>;

export const posRefundResponseSchema = posAdminCommandResponseSchema.extend({
  authorizationCode: z.string().nullable().optional(),
  operationNumber: z.string().optional(),
});
export type PosRefundResponse = z.infer<typeof posRefundResponseSchema>;

export const posTotalsResponseSchema = posAdminCommandResponseSchema.extend({
  txCount: z.number().int().optional(),
  txTotal: z.number().int().optional(),
});
export type PosTotalsResponse = z.infer<typeof posTotalsResponseSchema>;

// ACK-only commands (polling, cambio a modo normal): the ACK itself is the
// entire response, so there is nothing beyond ok.
export const posPollResponseSchema = z.object({ ok: z.boolean() });
export type PosPollResponse = z.infer<typeof posPollResponseSchema>;

// POS Integrado /pos/lastSale wire shape (the older lastSaleResponseSchema
// above is the IM30 kiosk variant).
export const posLastSaleResponseSchema = z.object({
  available: z.boolean(),
  response: posSaleResponseSchema.optional(),
});
export type PosLastSaleResponse = z.infer<typeof posLastSaleResponseSchema>;

export const healthResponseSchema = z.object({
  bridgeVersion: z.string(),
  im30: z.object({
    connected: z.boolean(),
    lastPollAt: z.string().nullable(),
  }),
  printer: z.object({
    connected: z.boolean(),
    lastPrintAt: z.string().nullable(),
  }),
  pendingConfirms: z.number().int().nonnegative(),
});
export type HealthResponse = z.infer<typeof healthResponseSchema>;

export const wsEventSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("im30.status"),
    code: z.string(),
    message: z.string(),
  }),
  z.object({
    type: z.literal("im30.connection"),
    connected: z.boolean(),
  }),
  z.object({
    type: z.literal("printer.status"),
    connected: z.boolean(),
    detail: z.string().optional(),
  }),
  z.object({
    type: z.literal("bridge.health"),
    pendingConfirms: z.number().int().nonnegative(),
  }),
  z.object({ type: z.literal("pos.status"), code: z.string(), message: z.string() }),
]);
export type WsEvent = z.infer<typeof wsEventSchema>;
